import React, { Fragment, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { ErrorBoundary } from 'react-error-boundary';
import { usePopper } from 'react-popper';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import clsx from 'clsx';

import useUser from '@/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Hooks
import useScreenshot from '@/hooks/utils/useScreenshot';

import PropTypes from 'prop-types';

import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import Instagram from '@/components/icons/Instagram';
// Components
import ErrorAnalyticsFallback from '@/components/layouts/ErrorFallbacks/ErrorAnalyticsFallback';

import PaginationTable from './PaginationTable';

const PopoverTable = ({
    panel,
    children,
    className,
    buttonLabel,
    buttonTitle = '',
    ...restProps
}) => {
    const [referenceElement, setReferenceElement] = useState();
    const [popperElement, setPopperElement] = useState();
    let { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-end',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 20]
                }
            }
        ]
    });

    return (
        <Popover {...restProps}>
            {({ open }) => (
                <>
                    <PopoverButton
                        ref={setReferenceElement}
                        as="button"
                        aria-label={buttonLabel}
                        className={className}
                        title={buttonTitle}>
                        {React.Children.map(children, (child) => {
                            return React.cloneElement(child, {
                                open
                            });
                        })}
                    </PopoverButton>
                    <div ref={setPopperElement} style={styles.popper} {...attributes.popper}>
                        <Transition
                            show={open}
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1">
                            <PopoverPanel className="z-10">
                                <div className="w-56 rounded-md border border-white bg-white/80 font-GSemiBold shadow-icoXL backdrop-blur-sm firefox:bg-gradient-to-tr firefox:from-icoGrayFirefox-300 firefox:to-icoWhiteFirefox dark:border-icoDarkMode-deepBlack dark:bg-icoDarkMode-anthracite/80">
                                    {panel}
                                </div>
                            </PopoverPanel>
                        </Transition>
                    </div>
                </>
            )}
        </Popover>
    );
};

PopoverTable.propTypes = {
    panel: PropTypes.node,
    children: PropTypes.node,
    className: PropTypes.string,
    buttonLabel: PropTypes.string
};

const buttonClass =
    'w-10 h-10 rounded-full border border-icoGray-300 dark:border-0 dark:bg-icoDarkMode-deepBlack dark:text-icoDarkmode-wolf flex items-center justify-center text-icoGray-500';

const listDashboardPopoverClass =
    'rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack';

const AnalyticsTable = ({
    data = [],
    userNumbersFormat,
    config,
    isLoading,
    network = 'instagram',
    onReset = () => {
        /** */
    }
}) => {
    const { t } = useTranslation('analytics');
    const { tableData, csvData } = useMemo(() => {
        const csvData = [];

        data.forEach((item) => {
            if (item?.user?.name) {
                csvData.push({ ...item, user: item.user.name });
            }
        });

        return { tableData: [...data], csvData };
    }, [data]);

    const { generatePng } = useScreenshot();

    const screenshotRef = useRef(null);

    const { user } = useUser();
    const exportCSVDelimiter = user?.settings?.export_csv_delimiter;

    if (isLoading) {
        return (
            <div className="flex min-h-[26rem] animate-pulse flex-col space-y-3 rounded-md bg-white p-8 dark:bg-icoDarkMode-anthracite">
                <div className="flex items-center justify-between">
                    <div className="h-8 w-64 bg-icoGray-100 dark:bg-icoDarkMode-deepBlack"></div>
                    <div className="h-10 w-10 rounded-full bg-icoGray-100 dark:bg-icoDarkMode-deepBlack"></div>
                </div>
                <div className="h-full bg-icoGray-100 dark:bg-icoDarkMode-deepBlack"></div>
            </div>
        );
    }

    const { columnNames, sortBy } = config;
    const isEmptyTable = tableData.length === 0;

    return (
        <ErrorBoundary FallbackComponent={ErrorAnalyticsFallback} onReset={() => onReset()}>
            <div
                ref={screenshotRef}
                className="flex h-full w-full flex-col space-y-6 rounded-md bg-white pt-5 transition duration-200 hover:shadow-icoXL dark:bg-icoDarkMode-anthracite dark:hover:shadow-icoDarkXL">
                <div className="flex items-center justify-between px-8">
                    <div className="flex items-center space-x-3">
                        {network === 'instagram' ? (
                            <Instagram />
                        ) : (
                            <Icon
                                icon={network}
                                className="text-xl text-facebook dark:text-white"
                            />
                        )}
                        <span className="font-GSemiBold">{t(config?.title)}</span>
                        {config?.tooltip ? (
                            // Wrapp Tooltip in span to avoid Tippy warning
                            <span>
                                <Tooltip
                                    content={
                                        typeof config?.tooltip === 'string'
                                            ? t(config.tooltip)
                                            : config?.tooltip
                                    }
                                    placement="top"
                                    interactive={true}
                                    popperOptions={{
                                        // Prevent tooltip to pop in the wrong place
                                        strategy: 'absolute'
                                    }}>
                                    <Icon
                                        aria-label={t('commons:infos')}
                                        icon="help"
                                        className="text-xs text-icoGray-400 hover:text-icoGray-500 dark:text-icoDarkMode-wolf"
                                    />
                                </Tooltip>
                            </span>
                        ) : null}
                    </div>
                    <div id="popover" className="relative z-30">
                        <PopoverTable
                            className={buttonClass}
                            buttonLabel={t('commons:export')}
                            buttonTitle={t('commons:export')}
                            panel={
                                <ul className="space-y-1 p-1 text-sm">
                                    <li className={listDashboardPopoverClass}>
                                        <CSVLink
                                            className={clsx('block w-full p-2 text-left', {
                                                'cursor-not-allowed opacity-50': isEmptyTable
                                            })}
                                            filename={tableData.id}
                                            aria-label={t('analytics:export_as_csv')}
                                            data={csvData}
                                            onClick={() => {
                                                if (isEmptyTable) return false;
                                            }}
                                            separator={exportCSVDelimiter ?? ','}>
                                            {t('analytics:export_as_csv')}
                                        </CSVLink>
                                    </li>
                                    <li className={listDashboardPopoverClass}>
                                        <button
                                            className={clsx('w-full p-2 text-left', {
                                                'cursor-not-allowed opacity-50': isEmptyTable
                                            })}
                                            aria-label={t('export_as_png')}
                                            disabled={isEmptyTable}
                                            onClick={() =>
                                                generatePng(
                                                    screenshotRef.current,
                                                    t(config?.title),
                                                    {
                                                        filter: (node) => node.id !== `popover`
                                                    }
                                                )
                                            }>
                                            {t('analytics:export_as_png')}
                                        </button>
                                    </li>
                                </ul>
                            }>
                            <Icon icon="export" />
                        </PopoverTable>
                    </div>
                </div>
                {tableData.length > 0 ? (
                    <PaginationTable
                        data={tableData}
                        columnNames={columnNames}
                        sortBy={sortBy}
                        userNumbersFormat={userNumbersFormat}
                    />
                ) : (
                    <div className="flex h-full min-h-[274px] w-full items-center justify-center px-8">
                        <div className="flex flex-col items-center">
                            <div className="flex h-15 w-15 items-center justify-center rounded-full bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                                {network === 'instagram' ? (
                                    <Instagram />
                                ) : (
                                    <Icon
                                        icon={network}
                                        className="text-xl text-facebook dark:text-white"
                                    />
                                )}
                            </div>
                            <div data-testid="no-data" className="mt-5 text-xs text-icoGray-500">
                                {t('analytics:no_data_period')}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};

AnalyticsTable.propTypes = {
    formatterTranslation: PropTypes.string,
    data: PropTypes.array,
    onReset: PropTypes.func,
    timeFormat: PropTypes.func,
    userNumbersFormat: PropTypes.object,
    valueFormat: PropTypes.object,
    network: PropTypes.string,
    config: PropTypes.object,
    isLoading: PropTypes.bool
};

export default AnalyticsTable;
